import {MdFrameSpec} from "../frame/MdFrameSpec";
import {UnitSystem} from "../../../../../../api/Projects";
import {HardwareGroupWithContent, HardwareItem} from "../../../../../../api/Hardware";
import {Opening} from "../../../../../../api/Openings";
import {ImperialDistance} from "../../../../../../misc/ImperialDistance";
import {inches} from "../../const";
import {MdDoorSpec} from "../door/MdDoorSpec";
import {EventEmitter2} from "../../util/EventEmitter2";
import {FrmVar} from "../../drawfrm/FrmVar";
import {calcDefaultHingeProduct, getHingeLines} from "../../../hardware/hingeProducts";
import {PrepDetail} from "../../../../../../api/QuoteHardware";
import {serFrmVar, serScalar, restoreFrmVar, restoreScalar} from "../../serialize/frmVar";
import {HingesDesign} from "../../../../../../api/elevationTool/HardwareElevationData";
import {parsePrepDrawingUnits} from "../../util/DrawingUnits";

// hinge config keys, kept in sync with the prep menu.
// see pages/project/shopdrawing/extras/PrepMenuOptions.tsx
const hinge45Std = "4.5\" STD";
const hinge5Std = "5\" STD";
const hinge45HW = "4.5\" HW";
const hinge5HW = "5\" HW";

// a door always keeps at least this many hinges; the count stepper won't go lower.
const MIN_HINGES = 1;

// default hinge knuckle height (the default kind is 4.5" STD), used to seed the
// first hinge's top offset before its kind is known.
const DEFAULT_HINGE_HEIGHT = 4.5 * inches;

export class MdHinges {

    // auto (non-overridden) spacing/count derived from the door height. simpleSpacing
    // seeds each new hinge's center-to-center gap; simpleCount drives the list length
    // until the user overrides it (see setCount / resetToAuto).
    simpleSpacing = new FrmVar("hardware.hinges.simpleSpacing", 10 * inches);
    simpleCount = new FrmVar("hardware.hinges.simpleCount", 2);

    list: MdHinge[] = [];
    listChanged = new EventEmitter2<string>("", "hinges.listChanged");

    // the group's default hinge product for the whole opening (both leaves): what a
    // hinge uses when it has no per-location override. null => derive from the specced
    // hinge lines (calcDefaultHingeProduct). Set from the Locations "Default Hinge"
    // ribbon selector.
    defaultProductOverride: number | null = null;

    // the frame height that drives the auto count/spacing. Captured in fromOpening so
    // resetToAuto can recompute the defaults on demand.
    private nominalHeight: FrmVar | null = null;

    static fromOpening(opening: Opening, hardware: HardwareGroupWithContent | null, sys: UnitSystem, frame: MdFrameSpec, door: MdDoorSpec) {
        const hinges = new MdHinges();
        hinges.nominalHeight = frame.nominalHeight;

        frame.nominalHeight.subscribeAndFireOnce(() => {
            // a manual count override survives a height change: leave the user's
            // count + per-hinge spacing alone, only the auto path rebuilds them.
            if(hinges.simpleCount.isModified()) return;
            hinges.applyAuto();
        });

        return hinges;
    }

    // serialize the hinge Design. offsetTop is derived (recomputeOffsets), so it isn't
    // stored.
    toDesign(): HingesDesign {
        return {
            simpleSpacing: serFrmVar(this.simpleSpacing),
            simpleCount: serScalar(this.simpleCount),
            defaultProductOverride: this.defaultProductOverride,
            list: this.list.map(h => ({
                id: h.id,
                spaceAbove: serFrmVar(h.spaceAbove),
                kind: h.kind,
                activeProductOverride: h.activeProductOverride,
                inactiveProductOverride: h.inactiveProductOverride,
            })),
        };
    }

    // rebuild from a stored Design: fromOpening wires the nominalHeight subscription,
    // then overlay the stored count/spacing, the hinge list, and the default product.
    // offsetTop is rebuilt from the spaceAbove chain by recomputeOffsets.
    static fromDesign(design: HingesDesign, opening: Opening, hardware: HardwareGroupWithContent | null, sys: UnitSystem, frame: MdFrameSpec, door: MdDoorSpec): MdHinges {
        const hinges = MdHinges.fromOpening(opening, hardware, sys, frame, door);

        restoreFrmVar(hinges.simpleSpacing, design.simpleSpacing);
        restoreScalar(hinges.simpleCount, design.simpleCount);

        hinges.list = design.list.map(h => {
            const spaceAbove = new FrmVar(`hardware.hinges[${h.id}].spaceAbove`, 0);
            restoreFrmVar(spaceAbove, h.spaceAbove);
            return {
                id: h.id,
                spaceAbove,
                offsetTop: new FrmVar(`hardware.hinges[${h.id}].offsetTop`, 0),
                kind: h.kind,
                activeProductOverride: h.activeProductOverride,
                inactiveProductOverride: h.inactiveProductOverride,
            };
        });

        hinges.defaultProductOverride = design.defaultProductOverride;
        hinges.recomputeOffsets();

        return hinges;
    }

    hingeHeightAt(topDownIndex: number) {
        const kind = this.list[topDownIndex].kind;
        switch(kind) {
            case hinge45Std:
            case hinge45HW:
                return 4.5 * inches;
            case hinge5Std:
            case hinge5HW:
                return 5 * inches;
            default:
                throw new Error(`Unknown hinge kind at index ${topDownIndex}: ${kind}`);
        }
    }

    // the auto top offset (top of frame jamb -> center of the first hinge). Matches
    // the legacy default: 9.75" less half the first hinge's knuckle height.
    private firstOffsetAuto(): number {
        const h = this.list.length > 0 ? this.hingeHeightAt(0) : DEFAULT_HINGE_HEIGHT;
        return 9.75 * inches - h / 2;
    }

    // the auto spaceAbove for the hinge at `index`: the top offset for the first
    // hinge, the shared center-to-center spacing for the rest.
    private autoSpaceAbove(index: number): number {
        return index === 0 ? this.firstOffsetAuto() : this.simpleSpacing.get();
    }

    private newHinge(index: number): MdHinge {
        const id = hingeIds.charAt(index).toString();
        return {
            id: id,
            spaceAbove: new FrmVar(`hardware.hinges[${id}].spaceAbove`, this.autoSpaceAbove(index)),
            offsetTop: new FrmVar(`hardware.hinges[${id}].offsetTop`, 0),
            kind: hinge45Std,
            activeProductOverride: null,
            inactiveProductOverride: null,
        };
    }

    // override (null clears) the hinge product for one location on one leaf, then
    // notify so the views redraw (the Locations knuckle label + its "Selected Hinge"
    // tab). Positions are shared across leaves; only the product differs.
    setProductOverride(hingeId: string, leaf: "active" | "inactive", productId: number | null) {
        const hinge = this.list.find(h => h.id === hingeId);
        if(!hinge) return;
        if(leaf === "active") hinge.activeProductOverride = productId;
        else hinge.inactiveProductOverride = productId;
        this.listChanged.emit("setProductOverride");
    }

    // the effective default hinge product for the whole opening (both leaves): the
    // user-set default when present, else the main hinge product derived from the
    // specced lines. Returns undefined when there are no hinge products.
    getDefaultProduct(hardware: HardwareItem[]): number | undefined {
        if(this.defaultProductOverride != null) return this.defaultProductOverride;
        return calcDefaultHingeProduct(getHingeLines(hardware));
    }

    // set (null clears back to auto) the group's default hinge product. The new
    // default applies to every hinge on both leaves, so drop all per-location
    // overrides to make them follow it, then notify so the views redraw.
    setDefaultProduct(productId: number | null) {
        this.defaultProductOverride = productId;
        for(const h of this.list) {
            h.activeProductOverride = null;
            h.inactiveProductOverride = null;
        }
        this.listChanged.emit("setDefaultProduct");
    }

    // seed the hinge list from the persisted group config: the custom spacing/count
    // (from prepConfigJson custom offsets) and the per-location product picks (from
    // each hinge item's hingePrepLocationConfig, set by the legacy Multi-Hinge
    // dialog). Called after the height-derived auto defaults are seeded so it layers
    // on top of them. No-op without a group.
    applyGroupConfig(hardwareGroup: HardwareGroupWithContent | null) {
        if(!hardwareGroup) return;
        this.applyCustomOffsets(hardwareGroup.prepConfigJson);
        this.applyProductOverrides(hardwareGroup.hardware);
    }

    // import the hinge spacing + count from the prep custom offsets. Preps a..e drive
    // the hinge chain: `a` is the top-of-jamb -> first-hinge-center offset, b..e the
    // center-to-center spacing for hinges 2..5. Each existing hinge has its prep
    // enabled; a prep's value (when set) overrides the auto spacing, null keeps the
    // calculated default. Marks the count + edited spacings modified so a later height
    // change won't reset them (matching a manual count override).
    private applyCustomOffsets(prepConfig: PrepDetail[] | undefined) {
        if(!prepConfig) return;

        const preps = hingePrepNames.map(n => prepConfig.find(p => p.name === n));

        // hinge count = number of enabled hinge preps. Bail if the config carries no
        // hinge preps (nothing to import; leave the auto count in place).
        const count = preps.filter(p => p?.enabled).length;
        if(count === 0) return;

        this.simpleCount.userSet(count, {debug: "hinges.applyCustomOffsets"});
        this.resizeList(count);

        this.list.forEach((h, i) => {
            const prep = preps[i];
            if(prep && prep.value) {
                h.spaceAbove.userSet(parsePrepDrawingUnits(prep.value), {debug: "hinges.applyCustomOffsets"});
            }
        });

        this.recomputeOffsets();
    }

    // import the per-location product picks. Each hinge item carries the (top-down)
    // location indexes where its product is used on each leaf; map those onto the
    // matching hinge's per-leaf override. A pick equal to the group's main hinge
    // product clears to null so the hinge follows the default (matching HingeSelection).
    private applyProductOverrides(hardware: HardwareItem[]) {
        const groupDefault = calcDefaultHingeProduct(getHingeLines(hardware));

        let changed = false;
        for(const item of hardware) {
            const cfg = item.hingePrepLocationConfig;
            if(!cfg) continue;

            for(const idx of cfg.active) {
                const hinge = this.list[idx];
                if(!hinge) continue;
                hinge.activeProductOverride = item.product === groupDefault ? null : item.product;
                changed = true;
            }

            for(const idx of cfg.inActive) {
                const hinge = this.list[idx];
                if(!hinge) continue;
                hinge.inactiveProductOverride = item.product === groupDefault ? null : item.product;
                changed = true;
            }
        }

        if(changed) this.listChanged.emit("applyProductOverrides");
    }

    // drop the default and per-location hinge product overrides whose product is no longer
    // in the opening's hardware group (the group's product set drifts as the hardware
    // schedule is edited). Returns the cleared product ids for the load-time reconcile
    // report. A cleared per-location override falls back to the group default; a cleared
    // default falls back to the calculated main hinge product.
    reconcileToGroup(hardware: HardwareItem[]): number[] {
        const valid = new Set(hardware.filter(i => !i.archived).map(i => i.product));
        const dropped: number[] = [];

        if(this.defaultProductOverride != null && !valid.has(this.defaultProductOverride)) {
            dropped.push(this.defaultProductOverride);
            this.defaultProductOverride = null;
        }

        for(const h of this.list) {
            if(h.activeProductOverride != null && !valid.has(h.activeProductOverride)) {
                dropped.push(h.activeProductOverride);
                h.activeProductOverride = null;
            }
            if(h.inactiveProductOverride != null && !valid.has(h.inactiveProductOverride)) {
                dropped.push(h.inactiveProductOverride);
                h.inactiveProductOverride = null;
            }
        }

        if(dropped.length > 0) this.listChanged.emit("reconcileToGroup");
        return dropped;
    }

    // grow/shrink the list to `count`, preserving existing hinges (and their edited
    // spacing). New hinges are appended with auto spacing.
    private resizeList(count: number) {
        if(this.list.length > count) {
            this.list = this.list.slice(0, count);
        }

        while(this.list.length < count) {
            this.list.push(this.newHinge(this.list.length));
        }
    }

    // walk the list, accumulating each hinge's spaceAbove into an absolute offsetTop
    // (measured from the top of the frame jamb), then notify subscribers. Call after
    // any spacing edit or list change.
    recomputeOffsets() {
        let cursor = 0;
        for(const h of this.list) {
            cursor += h.spaceAbove.get();
            h.offsetTop.set(cursor, {debug: "hinges.recomputeOffsets"});
        }

        this.listChanged.emit("recomputeOffsets");
    }

    // override the hinge count from the ribbon stepper. Marks the count modified so
    // it survives a later height change; existing hinges keep their edited spacing.
    setCount(count: number) {
        const next = Math.max(MIN_HINGES, Math.round(count));
        this.simpleCount.userSet(next, {debug: "hinges.setCount"});
        this.resizeList(next);
        this.recomputeOffsets();
    }

    // the auto spaceAbove for one hinge (used by a per-dimension "clear override").
    autoSpaceAboveFor(index: number): number {
        return this.autoSpaceAbove(index);
    }

    // apply the standard hinge spacing + count for a user-entered nominal height as a
    // custom override across every hinge: look the height up in the standard table
    // (getDefaultHingeDetail), set the count, and set each hinge's spaceAbove to the
    // table spacing (the first hinge keeps its standard top offset). Uses userSet so
    // the values read as modified — a later (actual door) height change won't reset
    // them. Drives the Locations "STD Height" action.
    applyStdHeight(nominalHeight: number) {
        const info = getDefaultHingeDetail(nominalHeight);

        this.simpleSpacing.userSet(info.hingeSpacing, {debug: "hinges.applyStdHeight"});
        this.simpleCount.userSet(info.hingeCount, {debug: "hinges.applyStdHeight"});
        this.resizeList(info.hingeCount);

        this.list.forEach((h, i) => {
            h.spaceAbove.userSet(this.autoSpaceAbove(i), {debug: "hinges.applyStdHeight"});
        });

        this.recomputeOffsets();
    }

    // recompute count + spacing from the door height and reset every hinge to auto,
    // clearing all manual overrides.
    resetToAuto() {
        this.simpleCount.clearModified();
        this.simpleSpacing.clearModified();
        this.applyAuto(true);
    }

    // apply the height-derived defaults: recompute count + spacing, rebuild the list,
    // and reset every hinge's spacing (clearing modified) to its auto value.
    private applyAuto(force = false) {
        if(!this.nominalHeight) return;

        const info = getDefaultHingeDetail(this.nominalHeight.get());
        this.simpleSpacing.set(info.hingeSpacing, {debug: "hinges.applyAuto"});
        this.simpleCount.set(info.hingeCount, {debug: "hinges.applyAuto"});

        this.resizeList(info.hingeCount);

        this.list.forEach((h, i) => {
            if(force || !h.spaceAbove.isModified()) {
                h.spaceAbove.set(this.autoSpaceAbove(i), {debug: "hinges.applyAuto"});
                h.spaceAbove.clearModified();
            }
        });

        this.recomputeOffsets();
    }
}

const hingeIds = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// prep custom-offset names for the hinge chain, top hinge first. Index i is the
// spacing for hinge i (see applyCustomOffsets / PrepCanvasManager).
const hingePrepNames = ["a", "b", "c", "d", "e"];

export type MdHingeKind =
    | typeof hinge45Std
    | typeof hinge5Std
    | typeof hinge45HW
    | typeof hinge5HW;

export type MdHinge = {
    id: string;
    // distance from the previous hinge's center to this hinge's center; for the
    // first hinge, the distance from the top of the frame jamb to its center.
    spaceAbove: FrmVar;
    // based on frame header as reference, measure to center of hinge. Derived from
    // the spaceAbove chain by recomputeOffsets(); read-only.
    offsetTop: FrmVar;
    kind: MdHingeKind;
    // the hinge product for this location, per leaf, when the user has picked a
    // non-default one (see the Locations "Selected Hinge" tab). null => use that
    // leaf's main hinge product (calcDefaultHingeProduct). The position is shared
    // across leaves — only the product may differ. inactiveProductOverride is unused
    // for a single opening. Stored per-hinge alongside spaceAbove.
    activeProductOverride: number | null;
    inactiveProductOverride: number | null;
}

function getDefaultHingeDetail(nominalHeight: number) {
    for(let i = 0; i < heights.length-1; i++) {
        if(heights[i+1].nominalHeight > nominalHeight) {
            return heights[i]
        }
    }

    return heights[heights.length - 1]
}

// see also PrepCanvasManager.ts
const heights: {nominalHeight: number, exactHeight: number, hingeSpacing: number, hingeCount: number}[] = [
    {nominalHeight: v("5'2"), exactHeight: v("61 1/8"), hingeSpacing: v("20 15/16"), hingeCount: 3},
    {nominalHeight: v("5'4"), exactHeight: v("63 1/8"), hingeSpacing: v("21 15/16"), hingeCount: 3},
    {nominalHeight: v("5'6"), exactHeight: v("65 1/8"), hingeSpacing: v("22 15/16"), hingeCount: 3},
    {nominalHeight: v("5'8"), exactHeight: v("67 1/8"), hingeSpacing: v("23 15/16"), hingeCount: 3},
    {nominalHeight: v("5'10"), exactHeight: v("69 1/8"), hingeSpacing: v("24 15/16"), hingeCount: 3},
    {nominalHeight: v("6'0"), exactHeight: v("71 1/8"), hingeSpacing: v("25 15/16"), hingeCount: 3},
    {nominalHeight: v("6'2"), exactHeight: v("73 1/8"), hingeSpacing: v("26 15/16"), hingeCount: 3},
    {nominalHeight: v("6'4"), exactHeight: v("75 1/8"), hingeSpacing: v("27 15/16"), hingeCount: 3},
    {nominalHeight: v("6'6"), exactHeight: v("77 1/8"), hingeSpacing: v("28 15/16"), hingeCount: 3},
    {nominalHeight: v("6'8"), exactHeight: v("79 1/8"), hingeSpacing: v("29 15/16"), hingeCount: 3},
    {nominalHeight: v("6'10"), exactHeight: v("81 1/8"), hingeSpacing: v("30 15/16"), hingeCount: 3},
    {nominalHeight: v("7'0"), exactHeight: v("83 1/8"), hingeSpacing: v("31 15/16"), hingeCount: 3},
    {nominalHeight: v("7'2"), exactHeight: v("85 1/8"), hingeSpacing: v("32 15/16"), hingeCount: 3},
    {nominalHeight: v("7'4"), exactHeight: v("87 1/8"), hingeSpacing: v("33 15/16"), hingeCount: 3},
    {nominalHeight: v("7'6"), exactHeight: v("89 1/8"), hingeSpacing: v("34 15/16"), hingeCount: 3},
    {nominalHeight: v("7'8"), exactHeight: v("91 1/8"), hingeSpacing: v("23 61/64"), hingeCount: 4},
    {nominalHeight: v("7'10"), exactHeight: v("93 1/8"), hingeSpacing: v("24 5/8"), hingeCount: 4},
    {nominalHeight: v("8'0"), exactHeight: v("95 1/8"), hingeSpacing: v("25 19/64"), hingeCount: 4},
    {nominalHeight: v("8'2"), exactHeight: v("97 1/8"), hingeSpacing: v("25 61/64"), hingeCount: 4},
    {nominalHeight: v("8'4"), exactHeight: v("99 1/8"), hingeSpacing: v("26 5/8"), hingeCount: 4},
    {nominalHeight: v("8'6"), exactHeight: v("101 1/8"), hingeSpacing: v("27 19/64"), hingeCount: 4},
    {nominalHeight: v("8'8"), exactHeight: v("103 1/8"), hingeSpacing: v("27 61/64"), hingeCount: 4},
    {nominalHeight: v("8'10"), exactHeight: v("105 1/8"), hingeSpacing: v("28 5/8"), hingeCount: 4},
    {nominalHeight: v("9'0"), exactHeight: v("107 1/8"), hingeSpacing: v("29 19/64"), hingeCount: 4},
    {nominalHeight: v("9'2"), exactHeight: v("109 1/8"), hingeSpacing: v("29 61/64"), hingeCount: 4},
    {nominalHeight: v("9'4"), exactHeight: v("111 1/8"), hingeSpacing: v("30 5/8"), hingeCount: 4},
    {nominalHeight: v("9'6"), exactHeight: v("113 1/8"), hingeSpacing: v("31 19/64"), hingeCount: 4},
    {nominalHeight: v("9'8"), exactHeight: v("115 1/8"), hingeSpacing: v("31 61/64"), hingeCount: 4},
    {nominalHeight: v("9'10"), exactHeight: v("117 1/8"), hingeSpacing: v("32 5/8"), hingeCount: 4},
    {nominalHeight: v("10'0"), exactHeight: v("119 1/8"), hingeSpacing: v("33 19/64"), hingeCount: 4},
    {nominalHeight: v("10'2"), exactHeight: v("121 1/8"), hingeSpacing: v("25 15/32"), hingeCount: 5},
    {nominalHeight: v("10'4"), exactHeight: v("123 1/8"), hingeSpacing: v("25 31/32"), hingeCount: 5},
    {nominalHeight: v("10'6"), exactHeight: v("125 1/8"), hingeSpacing: v("26 15/32"), hingeCount: 5},
    {nominalHeight: v("10'8"), exactHeight: v("127 1/8"), hingeSpacing: v("26 31/32"), hingeCount: 5},
    {nominalHeight: v("10'10"), exactHeight: v("129 1/8"), hingeSpacing: v("27 15/32"), hingeCount: 5},
    {nominalHeight: v("11'0"), exactHeight: v("131 1/8"), hingeSpacing: v("27 31/32"), hingeCount: 5},
]

function v(input: string) {
    return ImperialDistance.parse(input).inchesFlt() * inches;
}
